import { Button, Col, Row} from "react-bootstrap";
import { CampaignDto, UseGetCampaingsObjectResult} from "../types";
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addFilter } from "../../../../redux/filtersSlice";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { deleteCampaign, endCampaign, useCampaings } from "../api/campaignAPI";
import { RootState } from "../../../../redux/store";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { SelectedFilter } from "../../../../components/Filters/types";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import Search from "../../../../components/Search";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { getCampaingModalMotiveDeleteSchema } from "../schema/campaignSchema";
import { CampaignStatusEnum } from "../types/CampaignStatusEnum";
import { useCampaignClosedReasonSelect } from "../../Dictionaries/hooks/useCampaignClosedReasonSelect";
import { CampaignEndRequestDto } from "../../../../types/DeleteReasonDto";


export default function Campaign() {

    //basic imports
    const { t } = useTranslation(['common', 'campaign']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const currentPage = useSelector((state: RootState) => state.historical.campaigns.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.campaigns.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.campaigns.searchText);

    const [searchTerm, setSearchTerm] = useState("");
    const [appliedFilters] = useState<SelectedFilter[]>([]);

    const { campaignClosedReasonData, fetchCampaignClosedReasonData } = useCampaignClosedReasonSelect();

    const onEndDateChange = (date: Date | null) => {
        setEndDate(date)
    };
    const [campaignClosedReasonId, setCampaignClosedReasonId] = useState<number | null>();

    const handleSelectChange = (value: string | null) => {
        setCampaignClosedReasonId(Number(value));
    };

    const { data: campaingsPagination, isLoading, error, refetch } = useCampaings({
        search: searchTerm,
        filters: appliedFilters,
    });

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "campaigns", page: 1 }));
        dispatch(setSearchText({ table: "campaigns", text: '' }));
        setSearchTerm("");
    }

    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "campaigns", page: 1 }));
    };

    const columns: ColumnConfig<CampaignDto>[] = [
        { header: t("campaign:headerTableCampaign1"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("campaign:headerTableCampaign2"), key: "name" },
        {
            header: t("campaign:headerTableCampaign3"),
            key: "status",
            render: (item) => {
                const statusKey = Object.keys(CampaignStatusEnum).find(
                    key => CampaignStatusEnum[key as keyof typeof CampaignStatusEnum] === item.status
                );
                return statusKey ? t(`campaign:status.${statusKey}`) : item.status;
            } },
        { header: t("campaign:headerTableCampaign4"), key: "startDate", render: (item) => <>{formatDate(item.startDate)}</> },
        { header: t("campaign:headerTableCampaign5"), key: "endDate", render: (item) => <>{formatDate(item.endDate)}</> },
        { header: t("campaign:headerTableCampaign6"), key: "surveyTotal"},
    ];

    const { hideAlert, alert, isVisible } = useAlert({ fromLocation: true });

    const { showModal, modalData, motiveDelete, openModal, closeModal, errorModal, setMotiveDelete, setErrorModal, currentId, setEndDate, endDate } = useModal<CampaignDto>();

  
    const confirmSubmitModal = async () => {
        const schema = getCampaingModalMotiveDeleteSchema(t);
        if (modalData.type === 'delete') {
            try {
                await schema.validate({ motiveDelete }, { abortEarly: false });

            } catch (err: any) {
                const validationErrors: { [key: string]: string | undefined } = {};
                err.inner?.forEach((error: any) => {
                    if (error.path) {validationErrors[error.path] = error.message;}});
                setErrorModal(validationErrors);
                return;
            }
            await deleteCampaign(currentId, motiveDelete ?? "");
            navigate('/admin/campaign', {
                state: { message: t("campaign:modal.messageDeleteCampaignSuccessfully"), shouldRefresh: true }
            });
        }
        if (modalData.type === 'end' && endDate) {

            const body: CampaignEndRequestDto = {
                endDate: endDate, 
                campaignClosedReasonId: campaignClosedReasonId
            };

            await endCampaign(currentId, body);

            navigate('/admin/campaign', {
                state: { message: t("campaign:modal.modalDeleteCloseSubTitleSuccessfully"), shouldRefresh: true }
            });
        }
        closeModal();
    }

   



    const handleOpenEndModal = async (data: CampaignDto) => {
        await fetchCampaignClosedReasonData();
        const subtitle = data.hasActiveOpenSurveys ? t('campaign:modal.modalDeleteCloseSubTitleHasActiveOpenSurveys') : t('campaign:modal.modalDeleteCloseSubTitle');
        openModal(data, t('campaign:modal.modalDeleteCloseTitle', { name: data.name }), subtitle, 'end', "");
    };

    return (
        <div>
            <ModalGlobal<UseGetCampaingsObjectResult>
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                handleMotiveDeleteChange={(motive: string | null) => setMotiveDelete(motive)}
                isLoading={isLoading}
                optionsMap={campaignClosedReasonData}
                endDate={endDate}
                onEndDateChange={onEndDateChange}
                handleSelectChange={handleSelectChange}
            />
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("campaign:titleCampaign")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate(`/admin/campaign/new-campaign`)}>
                        {t("campaign:buttonNewCampaign")}
                    </Button>
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}/>
                    )}
                </Col>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={searchText || ''}
                        onChange={(text) => dispatch(setSearchText({ table: "campaigns", text: text }))}
                        onSearch={handleSearchInput}
                        onClear={handleClear}/>
                </Col>
            </Row>

            <AppTable<CampaignDto>
                data={campaingsPagination?.items || []}
                columns={columns}
                emptyMessage={"No records found"}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={campaingsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="campaigns"
                onExportCsv={() => { }}
                onDelete={(item) => { openModal(item, t("campaign:modal.modalDeleteCampaignTitle"), t("campaign:modal.modalDeleteCampaignSubtitle"), "delete", "") }}
                onView={(id: number) => { navigate(`/admin/campaign/${id}`) }}
                onSurveys={(item) => {dispatch(addFilter({table: 'adminSurveys',
                        filter: {
                            name: 'campaignId',
                            description: t("surveyAdmin:headerTableSurvey3") || "Campaign",
                            values: [item.id.toString()]
                        }
                })); navigate('/admin/surveyList', { state: { shouldRefresh: true } })
                }}
                onEnd={handleOpenEndModal}
                sectionPermission={SectionPermissionTypeEnum.EVENTS}
            />
        </div>
    )
}